/**
 * ONE invoice per session, on the rail the buyer actually chose.
 *
 * The Mexican checkout offers two rails — a card charge through Stripe and a SPEI bank transfer
 * through `commerce-gate` — and the buyer may switch between them, walk back into the wizard,
 * change their pack, and come back. A session holds at most one open invoice, so every one of
 * those moves can leave behind an invoice that is now WRONG: priced for a superseded selection,
 * or minted on the rail they just switched away from.
 *
 * This module is the single place that resolves that, and it is built around three rules:
 *
 * 1. **READ before you create.** `POST /session/invoice` SUPERSEDES a pending invoice (cancels it
 *    and mints a new one) and refuses a paid/processing one (A8, backend `invoice/index.ts` around
 *    145-187). So POSTing to find out what exists would void the CLABE the buyer may already have
 *    pasted into their banking app: on SPEI the happy path is a single `GET`, and a re-entered
 *    checkout on an unchanged selection costs zero writes. On gate2way a PIX code is never reused,
 *    so the GET is only a paid check and ONE POST mints the fresh code. A freshly minted invoice is
 *    never cancelled; the only gate2way cancel is the safety net for a backend that echoes back the
 *    very invoice it was asked to supersede (`supersedesInvoiceId`).
 * 2. **Cancel only on positive evidence, and only what the backend just handed us.** The rules
 *    live in [`@/utils/session-invoice`](../../../../utils/session-invoice.ts) (`invoiceRejectionFor`)
 *    and are unit-tested there. An invoice we merely failed to PARSE is never cancelled.
 * 3. **Never cancel a settled invoice.** A `paid` row is terminal — a voucher covered the whole
 *    bill, or the money already arrived — and the caller skips straight to the confirmation.
 *
 * The de-duplication that keeps a radio toggle from costing requests at all is one layer up, in
 * the checkout hook: it memoises this call per (rail × selection).
 */
import axios from 'axios';

import {
  COMMERCE_GATE_PROVIDER,
  GATE2WAY_PROVIDER,
  STRIPE_PROVIDER,
  hasPaidInvoiceForSession,
  invoiceRejectionFor,
  isInvoiceOpen,
  isInvoicePaid,
  isInvoicePayableOn,
  isInvoicePending,
  isInstructionRail,
  readInvoiceProvider,
  type CheckoutProvider,
  type InvoiceRailEvidence,
  type SessionInvoice,
  type SessionInvoiceConfig,
} from '@/utils/session-invoice';
// `cancelSessionInvoice` posts to `${sessionApiUrl}/cancel-invoice`, which is provider-agnostic
// (it voids a session invoice by id). It lives in `@/utils/stripe` because that module owns all
// the shared session-invoice plumbing; re-using it here keeps ONE definition of the endpoint.
import { cancelSessionInvoice } from '@/utils/stripe';
import { invoiceAuthHeaders } from './commerce-gate';
import { reissuesOnCommit, type InstructionDetails } from '../lib/checkout-rails';
import { readPixDetails } from './pix-details';
import { readQaFinalizeUrl } from './qa-finalize';
import { hasPayableDetails, readSpeiDetails } from './spei-details';

export type { CheckoutProvider };
export { COMMERCE_GATE_PROVIDER, GATE2WAY_PROVIDER, STRIPE_PROVIDER };

/** A usable invoice for the requested rail. Exactly one of the three outcomes is populated. */
export interface CheckoutInvoice {
  /** Invoice id, for cancel/reissue and support. */
  invoiceId: string;
  /**
   * Already settled: a voucher covered the whole bill, or the money arrived before this checkout
   * opened. Nothing left to collect, so the caller goes straight to the confirmation instead of
   * asking for a card or a transfer.
   */
  paid: boolean;
  /** Card rail: the Stripe PaymentIntent client secret to confirm in the browser. */
  clientSecret: string | null;
  /** Instruction rail: the bank coordinates (SPEI) or the PIX code to pay to. */
  details: InstructionDetails | null;
  /**
   * TEST-ONLY: the commerce-gate `test_finalize_payment_url`, on a SEPARATE channel from
   * {@link details} so the buyer-facing SPEI mapping stays provably clean. Non-null only in a
   * non-production environment when the backend attached one (see `data/qa-finalize`); `null`
   * on the card rail and in production. Drives the QA "simulate settlement" affordance.
   */
  qaFinalizeUrl: string | null;
}

export interface CheckoutInvoiceRequest {
  sessionId: string;
  /** The rail the buyer is on. */
  provider: CheckoutProvider;
  /** Live `configuration_data`, for the staleness comparison. */
  config: SessionInvoiceConfig | undefined | null;
  /** REQUIRED for every instruction rail (SPEI, PIX): the name the payment is registered under. */
  fullName?: string;
  voucherCode?: string | null;
  /**
   * gate2way only: the id of the PIX invoice the buyer was previously shown (the hook's shown id,
   * else `session.invoiceId`; NEVER the GET read, which is `findFirst` without `orderBy`). The POST
   * is expected to supersede it (A8); if the backend hands this same id back instead, the invoice
   * is cancelled and reissued (safety net for an idempotent backend).
   */
  supersedesInvoiceId?: string | null;
}

/** Reissue-loop bounds. A cancel propagates asynchronously, so for a few seconds `/invoice`
 *  keeps handing back the invoice we just voided; poll with a capped backoff before giving up.
 *  Only the (rare) reissue path pays this cost — the happy path never waits. */
const REISSUE_TIMEOUT_MS = 20000;
const REISSUE_MIN_DELAY_MS = 500;
const REISSUE_MAX_DELAY_MS = 1500;
const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/** Opt-in tracing. Enable in the browser with `localStorage.debug = 'invoice'` to log each
 *  read / create / cancel decision — off (silent) by default. Mirrors `@/utils/stripe`. */
function invoiceDebug(...args: unknown[]): void {
  try {
    if ((localStorage.getItem('debug')?.indexOf('invoice') ?? -1) !== -1) {
      // eslint-disable-next-line no-console
      console.log('[invoice]', ...args);
    }
  } catch {
    /* localStorage unavailable — tracing simply stays off */
  }
}

const invoiceTag = (invoice: SessionInvoice, evidence: InvoiceRailEvidence): string =>
  `${invoice?.id} (${invoice?.status}, ${invoice?.price}, rail=${readInvoiceProvider(invoice, evidence) ?? '?'})`;

/**
 * What this module can tell about an invoice that the shared rules can't work out alone: whether
 * it carries payable SPEI coordinates, and whether it carries a PIX code. Computed through the
 * flow's own wire mappings, so there is exactly ONE definition of "this row is a bank transfer"
 * and ONE of "this row is a PIX payment".
 */
const railEvidence = (invoice: SessionInvoice): InvoiceRailEvidence => ({
  hasSpeiCoordinates: hasPayableDetails(readSpeiDetails(invoice)),
  hasPixCode: readPixDetails(invoice) != null,
});

/** READ the session's current invoice — `GET ${sessionApiUrl}/invoice?session_id=…`.
 *
 *  The counterpart to the POST, and the only safe way to find out what a session already holds:
 *  POSTing supersedes the invoice, which on the SPEI rail voids a CLABE the buyer may already be
 *  transferring to. Returns `null` when the session has no invoice yet (or the read fails), so
 *  the caller falls through to creating one.
 *
 *  Auth is optional on this route (it resolves purely from `session_id`); the header is sent
 *  anyway for consistency with the rest of the invoice endpoints. */
export async function fetchSessionInvoice(sessionId: string): Promise<SessionInvoice | null> {
  try {
    const res = await axios.get(`${window.config.sessionApiUrl}/invoice`, {
      params: { session_id: sessionId },
      headers: invoiceAuthHeaders(),
    });
    const invoice = (res.data ?? null) as SessionInvoice | null;
    return invoice?.id ? invoice : null;
  } catch {
    return null;
  }
}

/** The session's `paid` invoice from the owner-scoped `POST ${userApiUrl}/invoice/list`, or `null`.
 *  `GET /session/invoice` answers with the session's FIRST invoice (often a cancelled one), so on a
 *  session with several invoices it cannot show that a later one was paid; this list can, pinned by
 *  owner (token) + session_id + status. `hasPaidInvoiceForSession` re-checks the row client-side, the
 *  same belt `sessionFetchInvoiceStatus` uses. A miss or a failed read is `null`, never a payment. */
async function fetchPaidSessionInvoice(sessionId: string): Promise<SessionInvoice | null> {
  try {
    const res = await axios.post(
      `${window.config.userApiUrl}/invoice/list`,
      {
        limit: 1,
        offset: 0,
        filter: [
          { name: 'session_id', op: 'eq', value: sessionId },
          { name: 'status', op: 'eq', value: 'paid' },
        ],
      },
      { headers: invoiceAuthHeaders() },
    );
    const rows: unknown = res.data?.data;
    if (!Array.isArray(rows)) return null;
    const row: unknown = rows.find((r: unknown) => hasPaidInvoiceForSession([r], sessionId));
    return row ? (row as SessionInvoice) : null;
  } catch {
    return null;
  }
}

/** Whether an `/invoice` error is the backend's "this voucher was already redeemed by this user"
 *  rejection (HTTP 500 with `error.code === 'VOUCHER_ALREADY_REDEEMED'`). The voucher is still
 *  applied from the session's persisted `configuration_data.voucher_code`, so the caller can
 *  safely retry WITHOUT the explicit param rather than block checkout. */
function isVoucherAlreadyRedeemed(err: unknown): boolean {
  const data = (err as { response?: { data?: { error?: { code?: string } } } })?.response?.data;
  return data?.error?.code === 'VOUCHER_ALREADY_REDEEMED';
}

/**
 * `POST ${sessionApiUrl}/invoice` on a chosen rail.
 *
 * `provider` is sent explicitly on BOTH rails. It is optional in the Joi schema (absent means
 * Stripe), but naming it is what makes a switch back to the card rail an actual instruction
 * rather than a default we happen to inherit.
 *
 * `full_name` rides along on every instruction rail. The backend guards it only for
 * `commerce-gate` (400 `"full_name is required for commerce-gate provider"`); it reaches gate2way
 * as `fullName`. It is meaningless on the card rail.
 *
 * `payment_method` is NOT sent until the Joi schema accepts it (Q9).
 */
async function createInvoiceOn(req: CheckoutInvoiceRequest): Promise<SessionInvoice> {
  const base: Record<string, unknown> = { session_id: req.sessionId, provider: req.provider };
  if (isInstructionRail(req.provider)) base.full_name = (req.fullName ?? '').trim();

  const post = async (body: Record<string, unknown>): Promise<SessionInvoice> => {
    const res = await axios.post(`${window.config.sessionApiUrl}/invoice`, body, {
      headers: invoiceAuthHeaders(),
    });
    return (res.data ?? {}) as SessionInvoice;
  };

  try {
    return await post(req.voucherCode ? { ...base, voucher_code: req.voucherCode } : base);
  } catch (err) {
    // The code is still applied from the session, so a second redemption attempt is the only
    // thing that failed — retry without it rather than blocking the checkout.
    if (req.voucherCode && isVoucherAlreadyRedeemed(err)) return post(base);
    throw err;
  }
}

/** What an instruction rail shows the buyer, tagged with its rail; `null` on the card rail, and
 *  on gate2way when the row carries no PIX code. */
function detailsFor(invoice: SessionInvoice, provider: CheckoutProvider): InstructionDetails | null {
  switch (provider) {
    case COMMERCE_GATE_PROVIDER:
      return { rail: 'commerce-gate', spei: readSpeiDetails(invoice) };
    case GATE2WAY_PROVIDER: {
      const pix = readPixDetails(invoice);
      return pix ? { rail: 'gate2way', pix } : null;
    }
    case STRIPE_PROVIDER:
      return null;
  }
}

/** Shape a usable invoice into the caller's result. */
function asResult(invoice: SessionInvoice, provider: CheckoutProvider): CheckoutInvoice {
  return {
    invoiceId: invoice.id,
    paid: isInvoicePaid(invoice),
    clientSecret: provider === STRIPE_PROVIDER ? invoice.provider_reference ?? null : null,
    details: detailsFor(invoice, provider),
    // commerce-gate only, through a separate reader that is itself env-gated: `null` in
    // production and on every other rail, so this is the ONLY place the test URL enters the channel.
    qaFinalizeUrl: provider === COMMERCE_GATE_PROVIDER ? readQaFinalizeUrl(invoice) : null,
  };
}

/**
 * The session already holds a paid or processing invoice: `POST /session/invoice` then answers
 * HTTP 200 with `{ code: 'SESSION_INVOICE_EXISTS' }` and no invoice at all. That answer alone does
 * not say the money arrived (a `processing` row is refused the same way), so the caller re-reads
 * the invoice and reports it settled only when the row itself says `paid`. Never cancelled.
 */
function isSessionInvoiceExists(body: SessionInvoice): boolean {
  return (body as { code?: unknown }).code === 'SESSION_INVOICE_EXISTS';
}

/** The error thrown when no usable invoice could be obtained, per rail. */
function noArtifactMessage(provider: CheckoutProvider): string {
  switch (provider) {
    case COMMERCE_GATE_PROVIDER:
      return 'commerce-gate invoice returned no CLABE';
    case GATE2WAY_PROVIDER:
      return 'gate2way invoice returned no PIX code';
    case STRIPE_PROVIDER:
      return 'stripe invoice returned no client secret';
  }
}

/** The invoice can be handed to the buyer as-is: settled, or carrying this rail's own artifact. */
function isUsable(
  invoice: SessionInvoice,
  req: CheckoutInvoiceRequest,
  evidence: InvoiceRailEvidence,
): boolean {
  if (isInvoicePaid(invoice)) return true;
  return (
    invoiceRejectionFor(invoice, req.config, req.provider, evidence) == null &&
    isInvoicePayableOn(invoice, req.provider, evidence)
  );
}

/**
 * Return an invoice for `provider` that is BOTH payable on that rail and priced against the live
 * selection, cancelling and reissuing the session's existing one only when it demonstrably can't
 * serve (see the module doc for the rules).
 *
 * Throws when no usable invoice can be obtained. Callers surface a retry rather than charging a
 * stale amount, or instructing a transfer to no account at all — and because this reads first,
 * that retry is cheap and self-healing: an artifact that attached a beat late is simply found.
 */
export async function reconcileCheckoutInvoice(req: CheckoutInvoiceRequest): Promise<CheckoutInvoice> {
  // gate2way never reuses a PIX code: the GET is only a paid check, and ONE POST mints fresh.
  const mintsFresh = reissuesOnCommit(req.provider);
  const existing = await fetchSessionInvoice(req.sessionId);

  if (existing) {
    const evidence = railEvidence(existing);
    invoiceDebug('existing', invoiceTag(existing, evidence), '→ want', req.provider);
    // Settled is TERMINAL. Never cancelled, never superseded, whichever rail asked: the money is
    // already in, and re-minting here would ask the buyer to pay a second time.
    if (isInvoicePaid(existing)) return asResult(existing, req.provider);
    if (!mintsFresh && isUsable(existing, req, evidence)) {
      invoiceDebug('reusing existing — no write');
      return asResult(existing, req.provider);
    }
    // Deliberately NOT cancelled here. The create below either supersedes it (one call, no
    // cancel) or hands it straight back — and only then, holding the row the backend actually
    // chose to give this rail, is voiding it justified.
  }

  let invoice = await createInvoiceOn(req);
  if (isSessionInvoiceExists(invoice)) {
    const held = await fetchSessionInvoice(req.sessionId);
    invoiceDebug('POST answered SESSION_INVOICE_EXISTS → re-read', held?.id, held?.status);
    if (held && isInvoicePaid(held)) return asResult(held, req.provider);
    // The GET is stale on a multi-invoice session (it answers with the first, often cancelled,
    // row), so ask the owner list for a paid row before calling it unpaid.
    const paid = await fetchPaidSessionInvoice(req.sessionId);
    invoiceDebug('owner list paid row', paid?.id);
    if (paid) return asResult(paid, req.provider);
    // A processing (or unreadable) row is NOT settlement: throwing surfaces the retry instead of
    // a paid confirmation and a Purchase for money that has not cleared.
    throw new Error('session invoice exists but is not paid yet');
  }
  let evidence = railEvidence(invoice);
  invoiceDebug('created', invoiceTag(invoice, evidence));

  // The only non-rule rejection: on gate2way the backend handed back the very invoice it was asked
  // to supersede (an idempotent POST). A NEW id is never cancelled here. `'idempotent-post'` is a
  // debug label only, deliberately kept out of `InvoiceRejection`.
  const rejection =
    invoiceRejectionFor(invoice, req.config, req.provider, evidence) ??
    (mintsFresh && req.supersedesInvoiceId != null && invoice.id === req.supersedesInvoiceId
      ? 'idempotent-post'
      : null);
  if (invoice.id && !isInvoicePaid(invoice) && rejection) {
    const rejectedId = invoice.id;
    invoiceDebug('unusable for', req.provider, '→', rejection, rejectedId);
    // Only a PENDING invoice can be voided; anything else is already closed and `cancel-invoice`
    // would just answer `INVOICE_NOT_PENDING`. And do NOT let a cancel error abort the reissue:
    // the endpoint can reject even when it voided the invoice server-side, and stopping here
    // would strand the buyer. The id check below is the real gate.
    if (isInvoicePending(invoice)) {
      try {
        await cancelSessionInvoice(req.sessionId, rejectedId);
        invoiceDebug('cancelled', rejectedId);
      } catch (err) {
        invoiceDebug('cancel threw — reissuing anyway', String(err));
      }
    }

    // The cancel propagates asynchronously: for a few seconds `/invoice` keeps returning the
    // just-voided invoice. Poll with a capped backoff until a genuinely new one arrives.
    //
    // The loop turns ONLY on "same id as the one we cancelled". A reissue that comes back with a
    // NEW id but still can't serve is a backend disagreement, not a propagation delay — retrying
    // it would just mint invoices — so it drops out here and fails the usability check below.
    const deadline = Date.now() + REISSUE_TIMEOUT_MS;
    let delay = REISSUE_MIN_DELAY_MS;
    invoice = await createInvoiceOn(req);
    evidence = railEvidence(invoice);
    invoiceDebug('reissued', invoiceTag(invoice, evidence));
    while (invoice.id === rejectedId) {
      if (Date.now() >= deadline) break;
      await sleep(delay);
      invoice = await createInvoiceOn(req);
      evidence = railEvidence(invoice);
      invoiceDebug('reissued', invoiceTag(invoice, evidence));
      delay = Math.min(Math.round(delay * 1.5), REISSUE_MAX_DELAY_MS);
    }
    if (invoice.id === rejectedId) {
      throw new Error('invoice reissue kept returning the cancelled invoice');
    }
  }

  if (!isUsable(invoice, req, evidence)) {
    throw new Error(noArtifactMessage(req.provider));
  }
  return asResult(invoice, req.provider);
}

/** What a session's CURRENT invoice says about where the buyer left off. */
export interface CheckoutInvoiceProbe {
  /** The rail it was minted on, or `null` when it can't be told. */
  provider: CheckoutProvider | null;
  /** The money is already in, or a voucher covered the whole bill. */
  paid: boolean;
  /**
   * The invoice as a ready-to-use result, when it can serve its OWN rail exactly as it stands.
   * `null` when it is stale or unpayable, i.e. when opening that rail will have to reissue.
   *
   * This is what lets a checkout reopen on the rail the buyer was already using without a second
   * round trip, and above all without ever POSTing — which on the SPEI rail would void the CLABE
   * they may be halfway through transferring to.
   */
  usable: CheckoutInvoice | null;
}

/**
 * READ where the buyer left off, so the checkout can reopen on the method they already committed
 * to instead of assuming the default and stranding a half-finished payment behind a collapsed
 * accordion.
 *
 * A read, never a write: this must not mint anything just to answer a question about the UI.
 * Returns `null` when the session has no invoice at all (a first visit).
 */
export async function probeCheckoutInvoice(
  sessionId: string,
  config: SessionInvoiceConfig | undefined | null,
): Promise<CheckoutInvoiceProbe | null> {
  const invoice = await fetchSessionInvoice(sessionId);
  if (!invoice) return null;

  const evidence = railEvidence(invoice);
  const paid = isInvoicePaid(invoice);
  invoiceDebug('probe', invoiceTag(invoice, evidence));

  // A CLOSED invoice says nothing about where the buyer is now, and this read hands us closed
  // ones routinely: `GET /session/invoice` is stale (probed live on test1 — after switching rails
  // it kept returning the session's FIRST invoice, cancelled, while `session.invoice_id` pointed
  // at the newest). Restoring a rail from it would reopen the checkout on a method the buyer had
  // already moved off. The caller falls back to `configuration_data.payment_method`, which is
  // what the buyer actually chose.
  const provider = isInvoiceOpen(invoice) ? readInvoiceProvider(invoice, evidence) : null;

  const usable =
    provider && isUsable(invoice, { sessionId, provider, config }, evidence)
      ? asResult(invoice, provider)
      : null;
  return { provider, paid, usable };
}
